import React, { useEffect, useMemo, useState } from "react";
import { useStateContext } from "contexts/ContextProvider";
import { Box, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import Grid from "@mui/material/Grid2";
import CardDashboardKPIChart from "components/CardDashboardKPIChart";
import {
  BarPlot,
  ChartsClipPath,
  ChartsLegend,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  legendClasses,
  LinePlot,
  pieArcLabelClasses,
  PiePlot,
  ResponsiveChartContainer,
} from "@mui/x-charts";
import CardDashboardItemTripleData from "components/CardDashboardItemHorizontalMulti";
import api from "services/api";

// Initialize static color map for consistent language colors
const DashboardAllServices = ({ user, dateSelection }) => {
  // Static property to store language color mapping
  DashboardAllServices.languageColorMap =
    DashboardAllServices.languageColorMap || {};
  const { refreshPage } = useStateContext();
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(true);

  const { handleErrorDialog } = useStateContext();

  const [servicesKPIs, setServicesKPIs] = useState();
  const [serviceDemandTrends, setServiceDemandTrends] = useState();
  const [pendingChecks, setPendingChecks] = useState();
  const [topLanguages, setTopLanguages] = useState();

  const [filterBy] = useState(null);

  // Extract year and months from dateSelection
  const selectedYear = dateSelection?.years?.[0] || dayjs().year();
  const selectedMonths = useMemo(
    () => dateSelection?.months || [],
    [dateSelection?.months],
  );

  // Convert month names to month numbers (1-12)
  const getMonthNumber = (monthName) => {
    const monthMap = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    };
    return monthMap[monthName] || 1;
  };

  // Convert selected months to month numbers
  const selectedMonthNumbers = useMemo(
    () => selectedMonths.map((month) => getMonthNumber(month)),
    [selectedMonths],
  );

  const params = useMemo(
    () => ({
      year: selectedYear,
      months: selectedMonthNumbers,
    }),
    [selectedYear, selectedMonthNumbers],
  );

  useEffect(() => {
    api
      .all([
        api.get("/dashboard/all/services/servicesCount", {
          params: params,
        }),
        api.get("/dashboard/all/services/serviceDemandTrends", {
          params: params,
        }),
        api.get("/dashboard/all/services/pendingChecks", {
          params: params,
        }),
        api.get("/dashboard/all/services/topLanguages", {
          params: params,
        }),
      ])
      .then(
        api.spread(
          (
            servicesKPIsResponse,
            serviceDemandTrendsResponse,
            pendingChecksResponse,
            topLanguagesResponse,
          ) => {
            if (servicesKPIsResponse) {
              setServicesKPIs(servicesKPIsResponse.data);
            }
            if (serviceDemandTrendsResponse) {
              setServiceDemandTrends(serviceDemandTrendsResponse.data);
            }
            if (pendingChecksResponse) {
              setPendingChecks(pendingChecksResponse.data);
            }
            if (topLanguagesResponse) {
              setTopLanguages(topLanguagesResponse.data);
            }

            //Set loading to false after all data is loaded
            setIsLoading(false);
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);

        setIsLoading(false); // Make sure to set loading to false on error
      });
  }, [refreshPage, filterBy, handleErrorDialog, params, theme]);

  const servicesInterpretationByStatusPieData = [
    {
      id: 0,
      value: servicesKPIs?.byStatus?.interpretation?.completed || "",
      label: "Completed",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: servicesKPIs?.byStatus?.interpretation?.confirmed || "",
      label: "Confirmed",
      color: theme.palette.info[500],
    },
    {
      id: 2,
      value: servicesKPIs?.byStatus?.interpretation?.requested || "",
      label: "Requested",
      color: theme.palette.warning[400],
    },
    {
      id: 3,
      value: servicesKPIs?.byStatus?.interpretation?.cancelled || "",
      label: "Cancelled",
      color: theme.palette.error[400],
    },
  ];

  const servicesTranslationByStatusPieData = [
    {
      id: 0,
      value: servicesKPIs?.byStatus?.translation?.completed || "",
      label: "Completed",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: servicesKPIs?.byStatus?.translation?.delivered || "",
      label: "Delivered",
      color: theme.palette.info[500],
    },
    {
      id: 2,
      value: servicesKPIs?.byStatus?.translation?.inProgress || "",
      label: "In Progress",
      color: theme.palette.warning[400],
    },
  ];

  const servicesOtherByStatusPieData = [
    {
      id: 0,
      value: servicesKPIs?.byStatus?.otherService?.completed || "",
      label: "Completed",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: servicesKPIs?.byStatus?.otherService?.delivered || "",
      label: "Delivered",
      color: theme.palette.info[500],
    },
    {
      id: 2,
      value: servicesKPIs?.byStatus?.otherService?.inProgress || "",
      label: "In Progress",
      color: theme.palette.warning[400],
    },
  ];

  const servicesInterpretationByTypePieData = [
    {
      id: 0,
      value: servicesKPIs?.byType?.interpretation.inPerson || "",
      label: "In Person",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: servicesKPIs?.byType?.interpretation.virtual || "",
      label: "Virtual",
      color: theme.palette.info[400],
    },
  ];

  const servicesTranslationByTypePieData = [
    {
      id: 0,
      value: servicesKPIs?.byType?.translation.translation || "",
      label: "Translation",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: servicesKPIs?.byType?.translation.proofreading || "",
      label: "Proofreading",
      color: theme.palette.info[400],
    },
    {
      id: 2,
      value: servicesKPIs?.byType?.translation.transcreation || "",
      label: "Transcreation",
      color: theme.palette.warning[400],
    },
  ];

  const servicesOtherByTypePieData = [
    {
      id: 0,
      value: servicesKPIs?.byType?.otherService.design || "",
      label: "Design",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: servicesKPIs?.byType?.otherService.voiceover || "",
      label: "Voiceover",
      color: theme.palette.info[400],
    },
    {
      id: 2,
      value: servicesKPIs?.byType?.otherService.consulting || "",
      label: "Consulting",
      color: theme.palette.info[400],
    },
    {
      id: 3,
      value: servicesKPIs?.byType?.otherService.retainerCredited || "",
      label: "Retainer Credited",
      color: theme.palette.warning[400],
    },
    {
      id: 4,
      value: servicesKPIs?.byType?.otherService.equipment || "",
      label: "Equipment",
      color: theme.palette.warning[400],
    },
    {
      id: 5,
      value: servicesKPIs?.byType?.otherService.other || "",
      label: "Other",
      color: theme.palette.error[400],
    },
  ];

  const getPie = (dataUnfiltered, id) => {
    // Check if dataUnfiltered is defined and is an array
    if (!dataUnfiltered || !Array.isArray(dataUnfiltered)) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.primary.text2,
              textAlign: "center",
            }}
          >
            No data available to display for the selected time period
          </Typography>
        </Box>
      );
    }

    const data = dataUnfiltered.filter((item) => {
      const numValue = Number(item.value);
      return !isNaN(numValue) && numValue !== 0;
    });

    // Check if there's any data after filtering
    const hasNoData = data.length === 0;

    // If no data, return a container with the noDataOverlay message
    if (hasNoData) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.primary.text2,
              textAlign: "center",
            }}
          >
            No data available to display for the selected time period
          </Typography>
        </Box>
      );
    }

    const TOTAL = data
      .map((item) => {
        const num = Number(item.value);
        return isNaN(num) ? 0 : num; // Replace NaN with 0 or handle as needed
      })
      .reduce((a, b) => a + b, 0);

    const getArcLabel = (params) => {
      const percent = params.value / TOTAL;

      return `${(percent * 100).toFixed(0)}%`;
    };

    return (
      <ResponsiveChartContainer
        series={[
          {
            type: "pie",
            data: data,
            id: id,
            highlightScope: { faded: "global", highlighted: "item" },
            animated: true,
            arcLabel: getArcLabel,
            arcLabelMinAngle: 20,
            innerRadius: 50,
            outerRadius: 100,
            paddingAngle: 2,
            cornerRadius: 10,
          },
        ]}
        margin={{ top: 100, bottom: 100, left: 0, right: 0 }}
        sx={{
          "& path": {
            stroke: "transparent", // set the outline to yellow
            strokeWidth: 5, // increase the stroke width to show the border
          },
          [`& .${pieArcLabelClasses.root}`]: {
            fill: theme.palette.background.default,
            fontSize: 14,
            fontWeight: "bold",
          },
          [`& .${legendClasses.mark}`]: {
            ry: 6,
          },
        }}
      >
        <ChartsTooltip
          trigger="item"
          labelFormatter={(itemData) => {
            return itemData.label || "";
          }}
          valueFormatter={(value, itemData) => {
            return `${value}${itemData.format === "percentage" ? "%" : ""}`;
          }}
        />

        <PiePlot />
      </ResponsiveChartContainer>
    );
  };

  const getLine = (data) => {
    // Early return if data is undefined or null
    if (!data) return null;

    // Create series configuration with proper type
    const seriesConfig = [
      {
        type: "line",
        dataKey: "totalServices",
        label: "Total Services",
        color: theme.palette.primary.main,
        showMark: false,
        curve: "natural",
        valueFormatter: (value) => (value === null ? "N/A" : value.toString()),
      },
      {
        type: "line",
        dataKey: "interpretationServices",
        label: "Interpretation",
        color: theme.palette.info[500],
        showMark: false,
        curve: "natural",
        valueFormatter: (value) => (value === null ? "N/A" : value.toString()),
      },
      {
        type: "line",
        dataKey: "translationServices",
        label: "Translation",
        color: theme.palette.warning[400],
        showMark: false,
        curve: "natural",
        valueFormatter: (value) => (value === null ? "N/A" : value.toString()),
      },
      {
        type: "line",
        dataKey: "otherServices",
        label: "Other Services",
        color: theme.palette.error[400],
        showMark: false,
        curve: "natural",
        valueFormatter: (value) => (value === null ? "N/A" : value.toString()),
      },
    ];

    // Check if required data properties exist
    if (
      !data.days ||
      !data.totalServices ||
      !data.interpretationServices ||
      !data.translationServices ||
      !data.otherServices
    ) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.primary.text2,
              textAlign: "center",
            }}
          >
            No data available to display for the selected time period
          </Typography>
        </Box>
      );
    }

    // Transform data for ResponsiveChartContainer
    const transformedData = data.days.map((day, index) => ({
      day,
      totalServices: data.totalServices[index] || 0,
      interpretationServices: data.interpretationServices[index] || 0,
      translationServices: data.translationServices[index] || 0,
      otherServices: data.otherServices[index] || 0,
    }));

    return (
      <ResponsiveChartContainer
        dataset={transformedData}
        series={seriesConfig}
        xAxis={[
          {
            scaleType: "point",
            dataKey: "day",
            tickLabelStyle: {
              angle: 0,
              fontSize: 12,
            },
          },
        ]}
        //height={400}
        margin={{ left: 40, right: 20, top: 20, bottom: 60 }}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "bottom", horizontal: "middle" },
            padding: 0,
          },
        }}
        sx={{
          "& .MuiLineElement-root": {
            strokeWidth: 4,
          },
        }}
      >
        <g clipPath={`url(#${"clipPathId"})`}>
          <LinePlot />
        </g>
        <ChartsClipPath id={"clipPathId"} />
        <ChartsXAxis />
        <ChartsYAxis />
        <ChartsTooltip />
        <ChartsLegend
          direction="row"
          position={{ vertical: "bottom", horizontal: "middle" }}
        />
      </ResponsiveChartContainer>
    );

    /* 
    // Old code with LineChart - kept for reference
    return (
      <LineChart
        xAxis={[
          {
            data: data?.days || [],
            scaleType: "point",
            tickLabelStyle: {
              angle: 0,
              fontSize: 12,
            },
          },
        ]}
        series={[
          {
            data: data?.totalServices || [],
            label: "Total Services",
            id: "totalServices",
            color: theme.palette.primary.main,
            showMark: false,
            curve: "natural",
            valueFormatter: (value) =>
              value === null ? "N/A" : value.toString(),
          },
          {
            data: data?.interpretationServices || [],
            label: "Interpretation",
            id: "interpretationServices",
            color: theme.palette.info[500],
            showMark: false,
            curve: "natural",
            valueFormatter: (value) =>
              value === null ? "N/A" : value.toString(),
          },
          {
            data: data?.translationServices || [],
            label: "Translation",
            id: "translationServices",
            color: theme.palette.warning[400],
            showMark: false,
            curve: "natural",
            valueFormatter: (value) =>
              value === null ? "N/A" : value.toString(),
          },
          {
            data: data?.otherServices || [],
            label: "Other Services",
            id: "otherServices",
            color: theme.palette.error[400],
            showMark: false,
            curve: "natural",
            valueFormatter: (value) =>
              value === null ? "N/A" : value.toString(),
          },
        ]}
        //width={500}
        height={400}
        margin={{ left: 40, right: 20, top: 20, bottom: 60 }}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "bottom", horizontal: "middle" },
            padding: 0,
          },
        }}
        sx={{
          "& .MuiLineElement-root": {
            //strokeDasharray: "20 5",
            strokeWidth: 4,
          },
        }}
      />
    );
    */
  };

  const getBar = (data, format) => {
    if (!data) return null;

    // Define color palette for languages
    const colorPalette = [
      theme.palette.primary.main,
      theme.palette.primary[300],
      theme.palette.primary[700],
      theme.palette.info.main,
      theme.palette.info[300],
      theme.palette.info[700],
      theme.palette.success.main,
      theme.palette.success[300],
      theme.palette.success[700],
      theme.palette.warning.main,
      theme.palette.warning[300],
      theme.palette.warning[700],
      theme.palette.error.main,
      theme.palette.error[300],
      theme.palette.error[700],
      theme.palette.primary[400],
      theme.palette.info[400],
      theme.palette.success[400],
    ];

    // Create a language color map if it doesn't exist in the component
    if (Object.keys(DashboardAllServices.languageColorMap).length === 0) {
      // Get all unique languages from all three datasets
      const allLanguages = new Set();

      if (topLanguages?.byTotalCount) {
        topLanguages.byTotalCount.forEach((item) =>
          allLanguages.add(item.name),
        );
      }

      if (topLanguages?.byTotalAccountCharge) {
        topLanguages.byTotalAccountCharge.forEach((item) =>
          allLanguages.add(item.name),
        );
      }

      if (topLanguages?.byTotalProfit) {
        topLanguages.byTotalProfit.forEach((item) =>
          allLanguages.add(item.name),
        );
      }

      // Create the mapping
      let colorIndex = 0;

      Array.from(allLanguages).forEach((language) => {
        DashboardAllServices.languageColorMap[language] =
          colorPalette[colorIndex % colorPalette.length];
        colorIndex++;
      });
    }

    // Check if data is an array before mapping
    if (!Array.isArray(data)) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.primary.text2,
              textAlign: "center",
            }}
          >
            No data available to display for the selected time period
          </Typography>
        </Box>
      );
    }

    // Get all language names and their corresponding colors for this dataset
    const languageNames = data.map((item) => item.name);
    const languageColors = data.map(
      (item) =>
        DashboardAllServices.languageColorMap[item.name] ||
        theme.palette.primary.main,
    );

    return (
      <ResponsiveChartContainer
        xAxis={[
          {
            scaleType: "band",
            dataKey: "name",
            tickLabelStyle: {
              angle: -30,
              textAnchor: "end",
              fontSize: 13,
            },
            colorMap: {
              type: "ordinal",
              values: languageNames,
              colors: languageColors,
              unknownColor: theme.palette.primary.main,
            },
          },
        ]}
        yAxis={[
          {
            scaleType: "sqrt",
          },
        ]}
        series={[
          {
            type: "bar",
            dataKey: "value",
            scaleType: "sqrt",
            valueFormatter: (code) => {
              if (format === "currency") {
                return `$${Number(code).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`;
              } else {
                return Number(code).toLocaleString("en-US", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                });
              }
            },
            cornerRadius: 4,
          },
        ]}
        dataset={data}
        margin={{ left: 40, top: 20, bottom: 65 }}
        sx={{
          //"& .MuiBarLabel-root": {
          //  fill: theme.palette.primary[100],
          //  fontSize: 14,
          //  fontWeight: "bold",
          //},
          [`& .${legendClasses.mark}`]: {
            ry: 6,
          },
        }}
        slotProps={{
          tooltip: {
            itemLabelFormatter: (params) => {
              const dataItem = data.find((item) => item.value === params.value);
              return dataItem ? dataItem.name : "";
            },
            valueFormatter: (value) => {
              if (format === "currency") {
                return `$${Number(value).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`;
              } else {
                return Number(value).toLocaleString("en-US", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                });
              }
            },
          },
        }}
      >
        <BarPlot borderRadius={10} />
        <ChartsXAxis position="bottom" />
        <ChartsYAxis />
        <ChartsTooltip />
      </ResponsiveChartContainer>
    );
  };

  return (
    <Grid container spacing={2} size={{ xs: 12 }}>
      <Grid
        size={{ xs: 12 }}
        container
        direction="row"
        sx={{
          //justifyContent: "center",
          alignItems: "stretch",
          px: 1,
          pt: 1,
        }}
      >
        <CardDashboardItemTripleData
          titles={[
            "Total Services",
            "Total Interpretations",
            "Total Translations",
            "Total Other Services",
          ]}
          contents={[
            servicesKPIs?.totalServices,
            servicesKPIs?.totalInterpretations,
            servicesKPIs?.totalTranslations,
            servicesKPIs?.totalOtherServices,
          ]}
          isLoading={isLoading}
        />
      </Grid>
      <Grid container spacing={2} size={{ xs: 12 }} sx={{ px: 1 }}>
        <Grid size={{ xs: 12, sm: 12 / 1, md: 12 / 1, lg: 12 / 3 }}>
          <CardDashboardKPIChart
            title="Interpretation Services By Status"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            chart={getPie(
              servicesInterpretationByStatusPieData,
              "Interpretation Status",
            )}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 2, md: 12 / 2, lg: 12 / 3 }}>
          <CardDashboardKPIChart
            title="Translation Services By Status"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            chart={getPie(
              servicesTranslationByStatusPieData,
              "Translation Status",
            )}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 2, md: 12 / 2, lg: 12 / 3 }}>
          <CardDashboardKPIChart
            title="Other Services By Status"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            chart={getPie(servicesOtherByStatusPieData, "Other Status")}
            isLoading={isLoading}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 12 / 1, md: 12 / 1, lg: 12 / 3 }}>
          <CardDashboardKPIChart
            title="Interpretation Services By Type"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            chart={getPie(servicesInterpretationByTypePieData, "Badge")}
            isLoading={isLoading}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 12 / 2, md: 12 / 2, lg: 12 / 3 }}>
          <CardDashboardKPIChart
            title="Translation Services By Type"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            chart={getPie(servicesTranslationByTypePieData, "W9")}
            isLoading={isLoading}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 12 / 2, md: 12 / 2, lg: 12 / 3 }}>
          <CardDashboardKPIChart
            title="Other Services By Type"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            chart={getPie(servicesOtherByTypePieData, "W9")}
            isLoading={isLoading}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 12 }}>
          <CardDashboardKPIChart
            title="Service Demand Trends"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            height={450}
            chart={getLine(serviceDemandTrends, "Translation Status")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          container
          direction="row"
          sx={{
            //justifyContent: "center",
            alignItems: "stretch",
          }}
        >
          <CardDashboardItemTripleData
            titles={[
              "Pending Checks for Interpretations",
              "Pending Checks for Translations",
              "Pending Checks for Other Services",
            ]}
            contents={[
              pendingChecks?.byCategory?.interpretation,
              pendingChecks?.byCategory?.translation,
              pendingChecks?.byCategory?.otherService,
            ]}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }}>
          <CardDashboardKPIChart
            title="Top 15 Languages By Number of Orders"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            height={350}
            chart={getBar(topLanguages?.byTotalCount, "number")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }}>
          <CardDashboardKPIChart
            title="Top 15 Languages By Account Charge"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            height={350}
            chart={getBar(topLanguages?.byTotalAccountCharge, "currency")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }} sx={{ pb: 1 }}>
          <CardDashboardKPIChart
            title="Top 15 Languages By Profit"
            formatType="Number"
            thisMonth={null}
            lastMonth={null}
            percentage={null}
            height={350}
            chart={getBar(topLanguages?.byTotalProfit, "currency")}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DashboardAllServices;
